import { EventStatus } from "@finbackoffice/enums";
import {
  CLIENT_CALENDAR,
  CLIENT_CALENDAR_TREE,
  CLIENT_FEED_LIVE,
  CLIENT_FEED_PREMATCH,
  SERVER_CALENDAR,
  SERVER_FEED_LIVE,
  SERVER_FEED_PREMATCH,
} from "constants/events";
import {
  ClientEventHandler,
  ClientSubscriptionParams,
  ClientsService,
  IMessagesFromServer,
  IMessagesToClient,
  SocketService,
} from "services";
import {
  FeedStorage,
  ICategoryFeedResponse,
  IEventFeedResponse,
  ISearchEventOptions,
  ISearchOptions,
  ITournamentFeedResponse,
} from "utils";
import { EventHandlingStrategy } from "./abstract.strategy";

export enum FeedStorageSubscriptionStatus {
  Pending = "Pending",
  Active = "Active",
  Inactive = "Inactive",
}

export class FeedStrategy extends EventHandlingStrategy {
  protected readonly feedStorages: Map<string, FeedStorage> = new Map<string, FeedStorage>();
  protected readonly subscriptionStatuses: Map<string, FeedStorageSubscriptionStatus> = new Map<
    string,
    FeedStorageSubscriptionStatus
  >();

  constructor(clientsService: ClientsService, socketService: SocketService) {
    super(clientsService, socketService);
  }

  static getEvents(): [IMessagesToClient, IMessagesFromServer["event"]][] {
    return [
      [CLIENT_FEED_LIVE, SERVER_FEED_LIVE],
      [CLIENT_FEED_PREMATCH, SERVER_FEED_PREMATCH],
      [CLIENT_CALENDAR_TREE, SERVER_CALENDAR],
    ];
  }

  on<T extends IMessagesToClient>(
    event: T,
    handler: ClientEventHandler<T>,
    params?: ClientSubscriptionParams<T>,
    statuses_to_delete?: EventStatus[],
  ): string {
    event = this.fromIncomingEventName(event) as T;

    const rid = super.on(event, handler, params as any);
    if (!this.feedStorages.has(rid)) {
      const storage = new FeedStorage(statuses_to_delete ?? [EventStatus.Ended]);
      this.feedStorages.set(rid, storage);
      this.subscriptionStatuses.set(rid, FeedStorageSubscriptionStatus.Pending);
    } else {
      // mimic server behavior and notify handler without server message because we already subscribbed
      if (this.subscriptionStatuses.get(rid) === FeedStorageSubscriptionStatus.Active) {
        this.clientsService.sendClientMessageByHandler(rid, { rid }, handler);
      }
    }
    return rid;
  }

  off<T extends IMessagesToClient>(event: T, handler: ClientEventHandler<T>): string | undefined {
    event = this.fromIncomingEventName(event) as T;

    const rid = super.off(event, handler);

    if (rid && !this.clientsService.hasListeners(rid)) {
      this.clearStorage(rid);
    }

    return rid;
  }

  public clear() {
    for (const rid of this.feedStorages.keys()) {
      this.clearStorage(rid);
    }
  }

  public handleServerMessage(message: IMessagesFromServer) {
    if (!this.subscriptionsStorage.isSubscriptionExist(message.rid)) {
      return;
    }

    const storage = this.feedStorages.get(message.rid);

    if (!storage) {
      // throw new Error(`Storage for RID ${message.rid} not found`);
      return;
    }

    if (this.subscriptionStatuses.get(message.rid) === FeedStorageSubscriptionStatus.Pending) {
      this.subscriptionStatuses.set(message.rid, FeedStorageSubscriptionStatus.Active);
    }

    switch (message.event) {
      case SERVER_FEED_LIVE:
        storage.update(message.data);
        this.clientsService.sendClientMessage(message.rid, { rid: message.rid });
        return;
      case SERVER_FEED_PREMATCH:
        storage.update(message.data);
        this.clientsService.sendClientMessage(message.rid, { rid: message.rid });
        return;
      case SERVER_CALENDAR:
        storage.update(message.data);
        this.clientsService.sendClientMessage(message.rid, { rid: message.rid });
        return;
      default:
        throw new Error(`Unknown event type: ${message.event}`);
    }
  }

  private getStorage(rid: string): FeedStorage | null {
    const storage = this.feedStorages.get(rid);
    if (!storage) {
      return null;
    }
    return storage;
  }

  public getSports(rid: string, options?: ISearchOptions) {
    const storage = this.getStorage(rid);
    return storage ? storage.getSports(options) : [];
  }

  public getCategories(rid: string, sportId: number, options?: ISearchOptions): ICategoryFeedResponse[] {
    const storage = this.getStorage(rid);
    return storage ? storage.getCategories(sportId, options) : [];
  }

  public getTournaments(rid: string, categoryId: number, options?: ISearchOptions): ITournamentFeedResponse[] {
    const storage = this.getStorage(rid);
    return storage ? storage.getTournaments(categoryId, options) : [];
  }

  public getEvents(rid: string, tournamentId: number, options?: ISearchEventOptions): IEventFeedResponse[] {
    const storage = this.getStorage(rid);
    return storage ? storage.getEvents(tournamentId, options) : [];
  }

  public getEventsBySportId(rid: string, id: number): IEventFeedResponse[] {
    const storage = this.getStorage(rid);
    return storage ? storage.getEventsBySportId(id) : [];
  }

  public getEvent(rid: string, id: number) {
    const storage = this.getStorage(rid);
    return storage ? storage.getEvent(id) : undefined;
  }

  public getSport(rid: string, id: number) {
    const storage = this.getStorage(rid);
    return storage ? storage.getSport(id) : undefined;
  }

  public getCategory(rid: string, id: number) {
    const storage = this.getStorage(rid);
    return storage ? storage.getCategory(id) : undefined;
  }

  public getTournament(rid: string, id: number) {
    const storage = this.getStorage(rid);
    return storage ? storage.getTournament(id) : undefined;
  }

  public getTopLeagues(rid: string) {
    const storage = this.getStorage(rid);
    return storage ? storage.getTopLeagues() : [];
  }

  private clearStorage(rid: string) {
    const storage = this.getStorage(rid);
    if (storage) {
      storage.clear();
    }
    this.feedStorages.delete(rid);
  }

  private fromIncomingEventName(event: IMessagesToClient): IMessagesToClient {
    if (event === CLIENT_CALENDAR_TREE) {
      event = CLIENT_CALENDAR;
    }
    return event;
  }
}
